import { useEffect, useState } from 'react';
import type { CreatePrintInput, UpdatePrintInput, PrintWithRelations, TenantPrinterWithDetails } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import { useTenantPrinters, useCreatePrint, useUpdatePrint } from '../hooks';
import { useTier } from '@/hooks/useAuth';

interface PrintFormProps {
  open: boolean;
  onClose: () => void;
  print?: PrintWithRelations | null;
  printerId?: string; // Pre-select printer if provided
}

// Form data type with string dates (from HTML inputs)
type PrintFormData = Omit<Partial<CreatePrintInput>, 'startedAt' | 'completedAt'> & {
  startedAt?: string;
  completedAt?: string;
};

const defaultFormData: PrintFormData = {
  name: '',
  status: 'IN_PROGRESS',
};

export function PrintForm({
  open,
  onClose,
  print,
  printerId,
}: PrintFormProps) {
  const [formData, setFormData] = useState<PrintFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: printersData } = useTenantPrinters();
  const createPrint = useCreatePrint();
  const updatePrint = useUpdatePrint();
  const { isBusiness } = useTier();

  const isEditing = !!print;
  const printers = printersData || [];

  // Reset form when opening/closing or print changes
  useEffect(() => {
    if (open) {
      if (print) {
        setFormData({
          name: print.name,
          fileName: print.fileName || undefined,
          startedAt: new Date(print.startedAt).toISOString().slice(0, 16),
          completedAt: print.completedAt ? new Date(print.completedAt).toISOString().slice(0, 16) : undefined,
          printDuration: print.printDuration || undefined,
          filamentUsedGrams: print.filamentUsedGrams || undefined,
          status: print.status,
          estimatedCost: print.estimatedCost || undefined,
          actualCost: print.actualCost || undefined,
          notes: print.notes || undefined,
        });
      } else {
        setFormData({
          ...defaultFormData,
          tenantPrinterId: printerId || '',
          startedAt: new Date().toISOString().slice(0, 16),
        });
      }
      setErrors({});
    }
  }, [open, print, printerId]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field when user makes a change
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!isEditing && !formData.tenantPrinterId) {
      newErrors.tenantPrinterId = 'Printer is required';
    }
    if (!formData.startedAt) {
      newErrors.startedAt = 'Start time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (isEditing && print) {
        const updateData: UpdatePrintInput = {
          name: formData.name!,
          fileName: formData.fileName || undefined,
          startedAt: new Date(formData.startedAt!),
          completedAt: formData.completedAt ? new Date(formData.completedAt) : undefined,
          printDuration: formData.printDuration || undefined,
          filamentUsedGrams: formData.filamentUsedGrams || undefined,
          status: formData.status || 'IN_PROGRESS',
          estimatedCost: formData.estimatedCost || undefined,
          actualCost: formData.actualCost || undefined,
          notes: formData.notes || undefined,
        };
        await updatePrint.mutateAsync({ id: print.id, input: updateData });
      } else {
        const createData: CreatePrintInput = {
          tenantPrinterId: formData.tenantPrinterId!,
          name: formData.name!,
          fileName: formData.fileName || undefined,
          startedAt: new Date(formData.startedAt!),
          completedAt: formData.completedAt ? new Date(formData.completedAt) : undefined,
          printDuration: formData.printDuration || undefined,
          filamentUsedGrams: formData.filamentUsedGrams || undefined,
          status: formData.status || 'IN_PROGRESS',
          estimatedCost: formData.estimatedCost || undefined,
          actualCost: formData.actualCost || undefined,
          notes: formData.notes || undefined,
        };
        await createPrint.mutateAsync(createData);
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const printerOptions = printers.map((p: TenantPrinterWithDetails) => ({
    value: p.id,
    label: p.instanceName || p.printer.name,
  }));

  const statusOptions = [
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Print Job' : 'Log Print Job'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          {/* Basic Info */}
          <FormSection title="Basic Info" defaultOpen>
            <div className="space-y-4">
              {!isEditing && (
                <Select
                  label="Printer"
                  value={formData.tenantPrinterId || ''}
                  onChange={(e) => handleChange('tenantPrinterId', e.target.value)}
                  error={errors.tenantPrinterId}
                  options={printerOptions}
                  required
                />
              )}
              <Input
                label="Job Name"
                placeholder="e.g., Benchy Test, Phone Stand"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                error={errors.name}
                required
              />
              <Input
                label="File Name"
                placeholder="e.g., benchy.3mf, phone_stand.gcode"
                value={formData.fileName || ''}
                onChange={(e) => handleChange('fileName', e.target.value || undefined)}
              />
              <Select
                label="Status"
                value={formData.status || 'IN_PROGRESS'}
                onChange={(e) => handleChange('status', e.target.value)}
                options={statusOptions}
              />
            </div>
          </FormSection>

          {/* Timing */}
          <FormSection title="Timing" defaultOpen>
            <div className="space-y-4">
              <Input
                label="Started At"
                type="datetime-local"
                value={formData.startedAt || ''}
                onChange={(e) => handleChange('startedAt', e.target.value)}
                error={errors.startedAt}
                required
              />
              <Input
                label="Completed At"
                type="datetime-local"
                value={formData.completedAt || ''}
                onChange={(e) => handleChange('completedAt', e.target.value || undefined)}
                
              />
              <Input
                label="Print Duration (hours)"
                type="number"
                step="0.1"
                placeholder="e.g., 2.5"
                value={formData.printDuration || ''}
                onChange={(e) => handleChange('printDuration', e.target.value ? parseFloat(e.target.value) : undefined)}
                
              />
            </div>
          </FormSection>

          {/* Material Usage */}
          <FormSection title="Material Usage">
            <div className="space-y-4">
              <Input
                label="Filament Used (grams)"
                type="number"
                step="0.1"
                placeholder="e.g., 45.5"
                value={formData.filamentUsedGrams || ''}
                onChange={(e) => handleChange('filamentUsedGrams', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
          </FormSection>

          {/* Cost Tracking */}
          <FormSection title="Cost Tracking">
            {isBusiness ? (
              <div className="space-y-4">
                <Input
                  label="Estimated Cost"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.estimatedCost || ''}
                  onChange={(e) => handleChange('estimatedCost', e.target.value ? parseFloat(e.target.value) : undefined)}
                />
                <Input
                  label="Actual Cost"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.actualCost || ''}
                  onChange={(e) => handleChange('actualCost', e.target.value ? parseFloat(e.target.value) : undefined)}
                />
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-background-subtle p-4">
                <p className="text-sm text-foreground-muted">
                  💼 Cost tracking is available on the Business tier.{' '}
                  <a href="/settings/subscription" className="text-primary hover:underline">
                    Upgrade to track costs
                  </a>
                </p>
              </div>
            )}
          </FormSection>

          {/* Notes */}
          <FormSection title="Notes">
            <Textarea
              placeholder="Add notes about this print job..."
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value || undefined)}
              rows={4}
            />
          </FormSection>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={createPrint.isPending || updatePrint.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={createPrint.isPending || updatePrint.isPending}>
            {isEditing ? 'Save Changes' : 'Log Print'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
